import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon, ArrowPathIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import CameraTagChip, { CAMERA_TAG_COLORS } from '../CameraTagChip';
import { Callout } from '../../common/Callout';
import {
  tagErrorMessage, type CameraTag, type CameraTagCategory, type CameraTagsApi, type PtzDetectStatus,
} from './cameraTagsApi';
import { useCameraTagCatalog } from './useCameraTagCatalog';
import TagCamerasModal from './TagCamerasModal';

/**
 * Settings → Camera tags: the vocabulary the camera list filters on. Categories hold tags
 * ("Department" → Traffic, Health care); a category is either any-of or one-per-camera.
 * Which camera carries which tag is set on the camera form or with the list's bulk "Tags…".
 */

const inputCls = 'rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground '
  + 'placeholder:text-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand outline-none';
const btnCls = 'inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-[12px] font-medium text-foreground hover:bg-muted disabled:opacity-50';
const iconBtn = 'rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground';

const COLOR_KEYS = Object.keys(CAMERA_TAG_COLORS);

function ColorDots({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  return (
    <div className="flex items-center gap-1">
      {COLOR_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          title={key}
          aria-label={`Colour ${key}`}
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className="h-4 w-4 rounded-full"
          style={{
            backgroundColor: CAMERA_TAG_COLORS[key],
            boxShadow: value === key ? `0 0 0 2px var(--card), 0 0 0 3.5px ${CAMERA_TAG_COLORS[key]}` : undefined,
          }}
        />
      ))}
    </div>
  );
}

/** One tag: the chip, its camera count, the camera picker, and inline edit/delete. */
function TagRow({ api, tag, category, onError }: {
  api: CameraTagsApi; tag: CameraTag; category: CameraTagCategory; onError: (message: string) => void;
}) {
  const [assigning, setAssigning] = useState(false);
  const { refresh } = useCameraTagCatalog(api);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color || 'slate');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.updateTag(tag.id, { name, color });
      await refresh();
      setEditing(false);
    } catch (err) {
      onError(tagErrorMessage(err, 'Could not save the tag'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.deleteTag(tag.id);
      await refresh();
    } catch (err) {
      onError(tagErrorMessage(err, 'Could not delete the tag'));
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className={`${inputCls} w-44`} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
        <ColorDots value={color} onChange={setColor} />
        <div className="ml-auto flex gap-1">
          <button onClick={save} disabled={busy || !name.trim()} className={iconBtn} aria-label="Save"><CheckIcon className="h-4 w-4" /></button>
          <button onClick={() => { setEditing(false); setName(tag.name); setColor(tag.color || 'slate'); }} className={iconBtn} aria-label="Cancel">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-muted">
      <CameraTagChip name={tag.name} color={tag.color} />
      {/* The count opens the picker too — "which 12?" is the natural next question. */}
      <button onClick={() => setAssigning(true)} className="text-[12px] tabular-nums text-muted-foreground hover:text-brand hover:underline">
        {tag.camera_count ?? 0} camera{tag.camera_count === 1 ? '' : 's'}
      </button>
      <TagCamerasModal api={api} isOpen={assigning} onClose={() => setAssigning(false)} tag={tag} category={category} />
      <div className="ml-auto flex items-center gap-1">
        {confirmDelete ? (
          <>
            <span className="text-[11px] text-muted-foreground">
              {tag.camera_count ? `Remove from ${tag.camera_count} camera${tag.camera_count === 1 ? '' : 's'}?` : 'Delete?'}
            </span>
            <button onClick={remove} disabled={busy} className="rounded px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: 'var(--destructive)' }}>Delete</button>
            <button onClick={() => setConfirmDelete(false)} className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground">Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setAssigning(true)} className={btnCls} title="Choose which cameras carry this tag">
              <VideoCameraIcon className="h-3.5 w-3.5" />Assign cameras
            </button>
            <button onClick={() => setEditing(true)} className={iconBtn} aria-label={`Edit ${tag.name}`}><PencilIcon className="h-3.5 w-3.5" /></button>
            <button onClick={() => setConfirmDelete(true)} className={iconBtn} aria-label={`Delete ${tag.name}`}><TrashIcon className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function CategoryCard({ api, category }: { api: CameraTagsApi; category: CameraTagCategory }) {
  const { refresh } = useCameraTagCatalog(api);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(category.name);
  const [newTag, setNewTag] = useState('');
  // New tags take the next colour round the palette, so a category doesn't come out all grey.
  const [newColor, setNewColor] = useState(COLOR_KEYS[(category.tags.length + 1) % COLOR_KEYS.length]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function run(fn: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refresh();
      return true;
    } catch (err) {
      setError(tagErrorMessage(err, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addTag(e: FormEvent) {
    e.preventDefault();
    if (!newTag.trim()) return;
    const ok = await run(() => api.createTag(category.id, { name: newTag, color: newColor }), 'Could not add the tag');
    if (ok) {
      setNewTag('');
      setNewColor(COLOR_KEYS[(category.tags.length + 2) % COLOR_KEYS.length]);
    }
  }

  const total = category.tags.reduce((n: number, t: CameraTag) => n + (t.camera_count || 0), 0);

  return (
    <div className="rounded-xl border border-border bg-background">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        {renaming ? (
          <div className="flex items-center gap-1">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className={`${inputCls} w-48`} autoFocus
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && await run(() => api.updateCategory(category.id, { name }), 'Could not rename')) setRenaming(false);
                if (e.key === 'Escape') { setRenaming(false); setName(category.name); }
              }} />
            <button
              onClick={async () => { if (await run(() => api.updateCategory(category.id, { name }), 'Could not rename')) setRenaming(false); }}
              disabled={busy || !name.trim()} className={iconBtn} aria-label="Save name"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button onClick={() => { setRenaming(false); setName(category.name); }} className={iconBtn} aria-label="Cancel"><XMarkIcon className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-[14px] font-semibold text-foreground">{category.name}</p>
            <button onClick={() => setRenaming(true)} className={iconBtn} aria-label={`Rename ${category.name}`}><PencilIcon className="h-3.5 w-3.5" /></button>
          </div>
        )}

        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground" title="One per camera: a camera can carry only one of these tags (Indoor OR Outdoor).">
          <input
            type="checkbox"
            checked={!category.multi_select}
            disabled={busy}
            onChange={(e) => run(() => api.updateCategory(category.id, { multi_select: !e.target.checked }), 'Could not change the setting')}
            className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand"
          />
          One per camera
        </label>

        <div className="ml-auto flex items-center gap-1">
          {confirmDelete ? (
            <>
              <span className="text-[11px] text-muted-foreground">
                Delete with its {category.tags.length} tag{category.tags.length === 1 ? '' : 's'}{total ? `, untagging ${total} assignment${total === 1 ? '' : 's'}` : ''}?
              </span>
              <button onClick={() => run(() => api.deleteCategory(category.id), 'Could not delete the category')} disabled={busy}
                className="rounded px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: 'var(--destructive)' }}>Delete</button>
              <button onClick={() => setConfirmDelete(false)} className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground">Cancel</button>
            </>
          ) : (
            <button onClick={() => setConfirmDelete(true)} className={iconBtn} aria-label={`Delete ${category.name}`}><TrashIcon className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      <div className="space-y-1 p-2">
        {error && <div className="px-2 pb-1"><Callout tone="danger">{error}</Callout></div>}
        {category.tags.map((t) => <TagRow key={t.id} api={api} tag={t} category={category} onError={setError} />)}
        {category.tags.length === 0 && <p className="px-2.5 py-1.5 text-[12px] text-muted-foreground">No tags yet.</p>}

        <form onSubmit={addTag} className="flex flex-wrap items-center gap-2 px-2.5 pt-2">
          <input value={newTag} onChange={(e) => setNewTag(e.target.value)} maxLength={60}
            placeholder={`Add a ${category.name.toLowerCase()} tag…`} className={`${inputCls} w-52`} />
          <ColorDots value={newColor} onChange={setNewColor} />
          <button type="submit" disabled={busy || !newTag.trim()} className={btnCls}><PlusIcon className="h-3.5 w-3.5" />Add</button>
        </form>
      </div>
    </div>
  );
}

/** The category list plus "add a category". */
export function CameraTagsManager({ api }: { api: CameraTagsApi }) {
  const { catalog, loaded, error: loadError, refresh } = useCameraTagCatalog(api);
  const [name, setName] = useState('');
  const [single, setSingle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { refresh(); }, [refresh]);

  async function addCategory(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.createCategory({ name, multi_select: !single });
      await refresh();
      setName('');
      setSingle(false);
    } catch (err) {
      setError(tagErrorMessage(err, 'Could not add the category'));
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) {
    return loadError ? <Callout tone="danger">{loadError}</Callout> : <p className="text-[12px] text-muted-foreground">Loading camera tags…</p>;
  }

  return (
    <div className="space-y-4">
      {catalog.categories.map((c) => <CategoryCard key={c.id} api={api} category={c} />)}

      <form onSubmit={addCategory} className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
          placeholder="New category, e.g. Zone or Vendor" className={`${inputCls} w-64`} />
        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <input type="checkbox" checked={single} onChange={(e) => setSingle(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border text-brand focus:ring-brand" />
          One per camera
        </label>
        <button type="submit" disabled={busy || !name.trim()} className={btnCls}><PlusIcon className="h-3.5 w-3.5" />Add category</button>
        {error && <div className="w-full"><Callout tone="danger">{error}</Callout></div>}
      </form>
    </div>
  );
}

/**
 * PTZ detection: how many cameras are PTZ / fixed / unknown, and a button that asks every
 * not-yet-checked camera over ONVIF. New cameras are checked automatically; this is for the
 * estate that existed before, and for re-checking.
 */
export function PtzDetectionCard({ api }: { api: CameraTagsApi }) {
  const { catalog, refresh } = useCameraTagCatalog(api);
  const [status, setStatus] = useState<PtzDetectStatus>({ running: false });
  const [error, setError] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function poll() {
    try {
      const s = await api.detectStatus();
      setStatus(s);
      if (s.running) {
        timer.current = setTimeout(poll, 2000);
      } else {
        refresh();
      }
    } catch (err) {
      setError(tagErrorMessage(err, 'Could not read detection progress'));
    }
  }

  useEffect(() => {
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function start(scope: 'unknown' | 'all') {
    setError('');
    try {
      setStatus(await api.startDetect(scope));
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(poll, 1000);
    } catch (err) {
      setError(tagErrorMessage(err, 'Could not start detection'));
    }
  }

  const { ptz } = catalog;
  const pct = status.total ? Math.round(((status.done || 0) / status.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {([['PTZ', ptz.yes], ['Fixed', ptz.no], ['Not detected', ptz.unknown]] as Array<[string, number]>).map(([label, n]) => (
          <div key={label} className="rounded-xl border border-border bg-background p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-1 text-[18px] font-semibold tabular-nums text-foreground">{n}</p>
          </div>
        ))}
      </div>

      {status.running ? (
        <div className="space-y-1.5">
          <div className="flex justify-between text-[12px] text-muted-foreground">
            <span>Checking cameras over ONVIF…</span>
            <span className="tabular-nums">{status.done}/{status.total}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: 'var(--brand)' }} />
          </div>
        </div>
      ) : status.finished_at && status.total !== undefined ? (
        <p className="text-[12px] text-muted-foreground">
          Last check: {status.total} camera{status.total === 1 ? '' : 's'}, {status.ptz_found} PTZ found
          {status.failed ? `, ${status.failed} didn't answer (offline or no ONVIF — set those by hand on the camera)` : ''}.
        </p>
      ) : null}

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => start('unknown')} disabled={status.running || ptz.unknown === 0} className={btnCls}>
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Detect for {ptz.unknown} unchecked camera{ptz.unknown === 1 ? '' : 's'}
        </button>
        <button onClick={() => start('all')} disabled={status.running} className={btnCls}>Re-check all cameras</button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        New cameras are checked automatically. A camera set to PTZ or Fixed by hand keeps that setting whatever ONVIF says.
      </p>
    </div>
  );
}
